/**
 * API Service for fetching data from Casinos Server API
 */

import fallbackStar from '../assets/images/star.svg';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const WEBSITE_ID = process.env.REACT_APP_WEBSITE_ID || '2'; // Jackpotify website ID

/**
 * Fetch operators (casinos) from Casinos Server API
 * Filters by website ID to show only jackpotify data
 * @returns {Promise<Array>} Array of casino objects
 */
export const fetchCasinos = async () => {
  try {
    // Use the public endpoint to get operators by website ID
    const endpoint = `/operators/by-website/${WEBSITE_ID}`
    const url = new URL(`${API_URL}${endpoint}`)
    url.searchParams.append('type', 'Casinos & Sportbooks');
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Transform API data to match our component structure
    if (Array.isArray(data)) {
      return data.map(operator => {
        // Handle logo - can be a URL or a path
        let imageUrl = null;
        if (operator.logo) {
          // If logo starts with http, use as is, otherwise prepend API URL
          imageUrl = operator.logo.startsWith('http') 
            ? operator.logo 
            : `${API_URL}${operator.logo}`;
        }
        
        // Prefer full description for homepage card content
        const description = operator.description || operator.shortDescription || '';
        const cleanDescription = description.trim().replace(/\s+/g, ' ').replace(/\n/g, ' ');
        
        // Preserve API casing (e.g., BetMGM)
        const name = operator.name || '';
        
        return {
          id: operator.id,
          name: name,
          description: cleanDescription,
          rating: operator.rating?.toString() || '0.0',
          url: operator.affiliateUrl || '#',
          image: imageUrl,
          starImage: fallbackStar,
          displayOrder: operator.displayOrder !== null && operator.displayOrder !== undefined 
            ? operator.displayOrder 
            : 999999, // Put null displayOrder at the end
        };
      }).sort((a, b) => {
        // Sort by displayOrder first (lower numbers first), then by rating if displayOrder is the same
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder;
        }
        // If displayOrder is the same, sort by rating descending
        return parseFloat(b.rating) - parseFloat(a.rating);
      });
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching casinos from API:', error);
    // Return empty array on error
    return [];
  }
};

export const fetchAlbertaCasinoSports = async () => {
  try {
    // Use the public endpoint to get operators by website ID
    const url = `${API_URL}/operators/by-website/${WEBSITE_ID}?type=alberta+casino+sports`;
    
    console.log('Fetching alberta casino sports casinos from');
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Transform API data to match our component structure
    if (Array.isArray(data)) {
      return data.map(operator => {
        // Handle logo - can be a URL or a path
        let imageUrl = null;
        if (operator.logo) {
          // If logo starts with http, use as is, otherwise prepend API URL
          imageUrl = operator.logo.startsWith('http') 
            ? operator.logo 
            : `${API_URL}${operator.logo}`;
        }
        
        // Prefer full description for homepage card content
        const description = operator.description || operator.shortDescription || '';
        const cleanDescription = description.trim().replace(/\s+/g, ' ').replace(/\n/g, ' ');
        
        // Preserve API casing (e.g., BetMGM)
        const name = operator.name || '';
        
        return {
          id: operator.id,
          name: name,
          description: cleanDescription,
          rating: operator.rating?.toString() || '0.0',
          url: operator.affiliateUrl || '#',
          image: imageUrl,
          starImage: fallbackStar,
          displayOrder: operator.displayOrder !== null && operator.displayOrder !== undefined 
            ? operator.displayOrder 
            : 999999, // Put null displayOrder at the end
        };
      }).sort((a, b) => {
        // Sort by displayOrder first (lower numbers first), then by rating if displayOrder is the same
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder;
        }
        // If displayOrder is the same, sort by rating descending
        return parseFloat(b.rating) - parseFloat(a.rating);
      });
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching casinos from API:', error);
    // Return empty array on error
    return [];
  }
};


/**
 * Fetch a single operator (casino) by ID
 * @param {number} id - Operator ID
 * @returns {Promise<Object>} Casino object
 */
export const fetchCasinoById = async (id) => {
  try {
    // Use the public endpoint to get operator by ID
    const url = `${API_URL}/public/operators/${id}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data) {
      // Handle logo - can be a URL or a path
      let imageUrl = null;
      if (data.logo) {
        // If logo starts with http, use as is, otherwise prepend API URL
        imageUrl = data.logo.startsWith('http') 
          ? data.logo 
          : `${API_URL}${data.logo}`;
      }
      
      // Prefer full description for detail content consistency
      const description = data.description || data.shortDescription || '';
      const cleanDescription = description.trim().replace(/\s+/g, ' ').replace(/\n/g, ' ');
      
      // Preserve API casing (e.g., BetMGM)
      const name = data.name || '';
      
      return {
        id: data.id,
        name: name,
        description: cleanDescription,
        rating: data.rating?.toString() || '0.0',
        url: data.affiliateUrl || '#',
        image: imageUrl,
        starImage: fallbackStar,
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching casino from API:', error);
    return null;
  }
};
